import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { useOutsideClick } from '../../../utils/custom-hooks';
import DropdownMenu from './dropdown-menu';

import './dropdown.scss';
import { getClass } from '../../../utils/component-helpers';

const DropdownToggleWrapper = props => {
  const {
    children,
    menuAlign,
    items,
    selected,
    onSelect,
    multi,
    searchable,
    confirmPrompt,
    onCancel,
    confirmText,
    cancelText,
    disabled,
    formElement,
    matchToggleWidth,
    itemRenderer,
    onChange,
    name,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [displayedItems, setDisplayedItems] = useState(items);
  const [currentSelected, setCurrentSelected] = useState(selected);

  const menuRef = useRef(false);

  useOutsideClick(
    menuRef,
    () => {
      if (isOpen) {
        if (confirmPrompt) {
          setCurrentSelected(selected);
        }

        setDisplayedItems(items);
        setIsOpen(false);
      }
    },
    () => {},
    [isOpen],
  );

  useEffect(() => {
    if (confirmPrompt) {
      setCurrentSelected(selected);
    }
  }, [selected, confirmPrompt]);

  useEffect(() => {
    setDisplayedItems(items);
  }, [items]);

  const onItemSelect = item => {
    let newSelected;
    if (!multi) {
      setIsOpen(false);
      newSelected = item;
    } else {
      const selectedList = confirmPrompt ? currentSelected.slice() : selected.slice();
      const itemIndex = selectedList.findIndex(selectedItem => selectedItem.value === item.value);

      if (itemIndex !== -1) {
        selectedList.splice(itemIndex, 1);
        newSelected = selectedList;
      } else {
        newSelected = confirmPrompt ? [...currentSelected, item] : [...selected, item];
      }
    }

    if (confirmPrompt) {
      setCurrentSelected(newSelected);
    } else {
      onSelect(newSelected);

      if (formElement) {
        onChange({
          target: { value: multi ? newSelected.map(s => s.value) : newSelected.value, name },
        });
      }
    }
  };

  const onSearch = query => {
    if (typeof query === 'undefined' || query === null) return;

    const newItems = items.filter(item => !item.label.toLowerCase().search(query.toLowerCase()));

    setDisplayedItems(newItems);
  };

  const onConfirmClick = () => {
    onSelect(currentSelected);

    if (formElement) {
      onChange({
        target: { value: multi ? currentSelected.map(s => s.value) : currentSelected.value, name },
      });
    }

    setIsOpen(false);
  };

  const onCancelClick = () => {
    onCancel();
    setCurrentSelected(selected);
    setIsOpen(false);
  };

  const toggleProps = {
    ...children.props,
    disabled: disabled || children.props.disabled,
    onClick: e => {
      if (children.props.onClick) {
        children.props.onClick(e);
      }
      setIsOpen(!isOpen);
    },
  };

  //  Checks if child is a native DOM Element or React Component. Prevents console warnings
  if (typeof children.type !== 'string') {
    toggleProps.isOpen = isOpen;
  }

  const ToggleElement = React.cloneElement(children, toggleProps);

  return (
    <div
      ref={menuRef}
      className={getClass('base-dropdown-container', { 'form-element': formElement })}
    >
      {ToggleElement}
      <DropdownMenu
        items={displayedItems}
        menuAlign={menuAlign}
        onSelect={onItemSelect}
        selected={confirmPrompt ? currentSelected : selected}
        isShowing={isOpen}
        multi={multi}
        onSearch={onSearch}
        searchable={searchable}
        confirmPrompt={confirmPrompt}
        onConfirm={onConfirmClick}
        onCancel={onCancelClick}
        confirmText={confirmText}
        cancelText={cancelText}
        containerRef={menuRef}
        matchToggleWidth={matchToggleWidth}
        itemRenderer={itemRenderer}
      />
    </div>
  );
};

DropdownToggleWrapper.propTypes = {
  onSelect: PropTypes.func,
  menuAlign: PropTypes.oneOf(['left', 'right']),
  multi: PropTypes.bool,
  searchable: PropTypes.bool,
  confirmPrompt: PropTypes.bool,
  onCancel: PropTypes.func,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  selected: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.any })),
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  matchToggleWidth: PropTypes.bool,
  itemRenderer: PropTypes.func,
};

DropdownToggleWrapper.defaultProps = {
  onSelect: () => {},
  menuAlign: 'right',
  multi: false,
  searchable: false,
  confirmPrompt: false,
  onCancel: () => {},
  confirmText: 'Done',
  cancelText: 'Cancel',
  selected: [],
  disabled: false,
  onChange: () => {},
  matchToggleWidth: false,
  itemRenderer: null,
};

export default DropdownToggleWrapper;
